import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { getCsrfHeaders } from "@/lib/queryClient";
import logoImage from "@assets/favicon new no background cc6633.png";
import { Link } from "wouter";

interface InviteData {
  valid: boolean;
  invitedEmail: string;
  ownerName: string;
  ownerCompany: string;
}

type PageState = "loading" | "valid" | "invalid" | "success";

export default function AcceptInvite() {
  const [, navigate] = useLocation();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (!token) {
      setErrorMessage("No invitation token provided.");
      setPageState("invalid");
      return;
    }

    fetch(`/api/account/invite/accept?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          setErrorMessage(data.error || "This invitation is no longer valid.");
          setPageState("invalid");
        } else {
          setInviteData(data);
          setPageState("valid");
        }
      })
      .catch(() => {
        setErrorMessage("Unable to validate the invitation. Please try again.");
        setPageState("invalid");
      });
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");

    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/account/invite/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        credentials: "include",
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data.error || "Failed to create your account. Please try again.");
      } else {
        setPageState("success");
      }
    } catch {
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={logoImage} alt="Fill The Trucks" className="h-12 object-contain" />
        </div>

        {pageState === "loading" && (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Validating your invitation...</p>
            </CardContent>
          </Card>
        )}

        {pageState === "valid" && inviteData && (
          <Card>
            <CardHeader>
              <CardTitle>You've been invited to Fill The Trucks</CardTitle>
              <CardDescription>
                {inviteData.ownerName} has invited you to access the{" "}
                <strong>{inviteData.ownerCompany}</strong> account.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={inviteData.invitedEmail ?? ""}
                    readOnly
                    className="bg-muted text-muted-foreground"
                    data-testid="input-invite-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Create a password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    required
                    data-testid="input-invite-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    data-testid="input-invite-confirm-password"
                  />
                </div>
                {submitError && (
                  <p className="text-sm text-destructive">{submitError}</p>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting}
                  data-testid="button-accept-invite"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating your account...
                    </>
                  ) : (
                    "Create My Access"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {pageState === "invalid" && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                <CardTitle>This invitation is no longer valid</CardTitle>
              </div>
              <CardDescription>{errorMessage}</CardDescription>
            </CardHeader>
            <CardContent>
              <Link href="/login">
                <Button variant="outline" className="w-full" data-testid="button-go-to-login-invalid">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {pageState === "success" && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <CardTitle>Account created</CardTitle>
              </div>
              <CardDescription>
                Your account has been created. You can now log in.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link href="/login">
                <Button className="w-full" data-testid="button-go-to-login-success">
                  Go to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
